import PlayerBlock from 'components/PlayerBlock';
import { Table, TableBody, TableHead, TableRow } from 'components/Table';
import { useGameContainer } from '../index';
import styles from './styles/GameLeaders.module.scss';
import { BASKETBALL, FOOTBALL } from 'constants/Sports';
import TeamImage from '../../../components/TeamImage';
import FlexContainer from '../../../components/FlexContainer';
import cn from 'classnames';
import { GamesShowQuery } from 'queries';
import ContentSection, { ContentSectionFooterButton, ContentSectionHeader } from '@/components/ContentSection';
import { SectionHeaderSize } from '@/components/SectionHeader';

function getLeaderCategories(sportId: number) {
  switch (sportId) {
    case BASKETBALL:
      return {
        points: ['field_goals_made', 'three_points_made', 'free_throws_made', 'points']
      };
    case FOOTBALL:
      return {
        passing: ['passing_completions', 'passing_attempts', 'passing_yards', 'yards_per_attempt', 'passing_touchdowns'],
        rushing: ['rushing_attempts', 'rushing_yards', 'rushing_yards_per_attempt', 'rushing_touchdowns'],
        receiving: ['receiving_receptions', 'receiving_yards', 'yards_per_reception', 'receiving_touchdowns']
      };
  }
}

interface GameLeaderRowProps {
  leader: NonNullable<GamesShowQuery['game']['gameTeams'][number]['leaders']>[number];
  currentStats: string[];
  genderSport: string;
  teamLogo?: string;
}

function GameLeaderRow({ leader, currentStats, genderSport, teamLogo }: GameLeaderRowProps) {
  if (!leader.game_player) return null; // todo this shouldnt be happening

  return (
    <TableRow>
      <td>
        <div className={styles.PlayerBlock}>
          <TeamImage alt="Team Logo" src={teamLogo} />
          <div className={styles.PlayerNumber}>{leader.game_player.jersey_number}</div>
          <PlayerBlock player={leader.game_player} genderSport={genderSport} v2 />
        </div>
      </td>
      {currentStats.map((stat, i) => (
        <td className={styles.GameLeadersStat} key={i}>
          {leader.game_player?.stat[stat as keyof NonNullable<(typeof leader)['game_player']>['stat']]}
        </td>
      ))}
    </TableRow>
  );
}

interface GameLeaderTableProps {
  gamePlayers: NonNullable<GamesShowQuery['game']['gameTeams'][number]['leaders']>;
}

function GameLeaderTable({ gamePlayers }: GameLeaderTableProps) {
  const [{ game, statCategories }] = useGameContainer();
  if (gamePlayers.length === 0) return null;

  const categories = getLeaderCategories(game.genderSport.sportId);
  if (!categories) return null;

  const currentCategory = gamePlayers[0].category as keyof typeof categories;

  const currentStats = categories[currentCategory];
  if (!currentStats) return null;

  const boxscoreCategory = currentCategory == 'points' ? 'primary' : currentCategory;
  const headers = currentStats.map(stat => statCategories[boxscoreCategory][stat]);

  return (
    <Table className={styles.GameLeadersTable} reducedPadding v2>
      <TableHead headers={[...headers]}>
        <th style={{ textAlign: 'left' }}>Team</th>
      </TableHead>
      <TableBody>
        {gamePlayers.map((leader, i) => (
          <GameLeaderRow
            key={i}
            leader={leader}
            currentStats={currentStats}
            genderSport={game.genderSport.slug}
            teamLogo={game.gameTeams.find(gameTeam => +gameTeam.id === leader.game_player?.game_team_id)?.team.image}
          />
        ))}
      </TableBody>
    </Table>
  );
}

export default function GameLeaders() {
  const [{ game }] = useGameContainer();

  const sportLeaderMap = getLeaderCategories(game.genderSport.sportId);
  if (!sportLeaderMap) return null;

  const homeLeaders = game.gameTeams[1].leaders || [];
  const awayLeaders = game.gameTeams[0].leaders || [];

  const gameLeaders = [awayLeaders, homeLeaders].flat();
  if (gameLeaders.length === 0) return null;

  return (
    <ContentSection>
      <ContentSectionHeader size={SectionHeaderSize.LARGE}>Stat Leaders</ContentSectionHeader>
      {game.genderSport.sportId === FOOTBALL &&
        Object.keys(sportLeaderMap)
          .map(key => {
            return gameLeaders.filter(leader => leader.category === key);
          })
          .map((leaders, i) => (
            <FlexContainer column key={`category-leaders-${i}`}>
              <div className={cn(styles.CategoryName, { [styles.CategoryOne]: i == 0 })}>{leaders[0].category}</div>
              <GameLeaderTable gamePlayers={leaders} key={i} />
            </FlexContainer>
          ))}
      {game.genderSport.sportId === BASKETBALL && (
        <>
          {awayLeaders.length > 0 && <GameLeaderTable gamePlayers={awayLeaders} />}
          {homeLeaders.length > 0 && <GameLeaderTable gamePlayers={homeLeaders} />}
        </>
      )}
      <ContentSectionFooterButton href={game.webBoxscorePath}>See Full Box Score</ContentSectionFooterButton>
    </ContentSection>
  );
}
